/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3c §3d] WHAT MAY BE DONE ON THIS SHEET — three inputs, five verbs, one notice.
 *
 *  ⚠️ IT IS CALLED `controls`, NEVER `readOnly`, AND THAT IS THE WHOLE POINT.
 *
 *  A single boolean was correct while there was one window. There are two now, and they close
 *  at different instants: naming an hour after a fixed-time duty starts, swapping when the shift
 *  ends. So on a running bandobast there is a real interval in which an operator may NOT name
 *  anybody and MAY still substitute somebody who did not turn up — and no boolean can say that.
 *
 *  ⚠️ `DutyOfficers.tsx` HAD ALREADY MADE THE COLLAPSE, AND IT WAS ALREADY WRONG. It wrote
 *  `canWrite = !readOnly && windowOpen` and passed `readOnly={!canWrite}` down, so under
 *  [PRE-P6.2]'s narrow swap window it disabled NAMING because SWAPPING had closed. This replaces
 *  that, rather than adding a second flag beside it.
 *
 *  ⚠️ THE CAPABILITY IS AN INPUT, NOT A CONSEQUENCE. `canWrite` answers "may this operator
 *  write at all", the windows answer "may this DUTY still change". They fail for different
 *  reasons and must say different things — telling a nodal their access is view-only when it is
 *  not is [C1f §7b]'s blocker-versus-refusal distinction, and it sends them to the wrong person.
 *
 *  ⚠️ AND IT IS PURE, so the whole policy is a table a unit test can drive. No React, no hooks,
 *  no clock: the server publishes both booleans on the fill sheet and this only combines them.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * At most ONE notice is ever shown. A stack of banners is a screen nobody reads.
 *
 * ⚠️ [amended C3c.1] `swapClosed` IS THE FOURTH, AND IT EXISTS BECAUSE THE TWO WINDOWS CAN
 * INVERT ON A PERFECTLY LEGAL ORDER.
 *
 * `namingWindowClosesAt` is `starts_at + 1h` and `swapWindowClosesAt` is `ends_at`, and NOTHING
 * constrains a shift to be an hour long — `timeOfDaySchema` takes any `HH:mm` pair. So a
 * 06:00–06:30 duty point closes SWAPPING at 06:30 and NAMING at 07:00, and between those two
 * instants `fillControlsFor` returned `{maySwap: false, notice: 'none'}`: `refusalFor` answered
 * `undefined`, no banner rendered, and Swap simply vanished from every `⋮` with no explanation.
 *
 * ⚠️ THE COMBINATION IS RARE AND THE SILENCE IS NOT ACCEPTABLE, which is §3c's own argument —
 * five verbs exist precisely so that a refusal can name its own reason. A notice set that cannot
 * express a reachable state is the `readOnly` collapse in a new costume.
 */
/**
 * ⚠️ [amended C3c.2] `shortfallOnly` IS THE FIFTH, AND IT EXISTS BECAUSE THE CAPABILITY ARM
 * STOPPED BEING ONE FACT.
 *
 * `noCapability` renders `READ_ONLY_TOOLTIP` — "Your access is view-only." — and until this pass
 * that was true of everybody it described. A `NODAL_MONITOR` now holds
 * `fulfilment.completeShortfall` and no other write, so on this screen they meet a footer with a
 * live write button in it and, on every naming control, a tooltip telling them their access
 * changes nothing. That is the collapse this whole file exists to refuse, arriving from the one
 * direction its four notices could not express.
 */
export const FILL_NOTICES = [
  'none',
  'noCapability',
  'shortfallOnly',
  'namingClosed',
  'swapClosed',
  'allClosed',
] as const;

export type FillNotice = (typeof FILL_NOTICES)[number];

export type FillControls = {
  mayName: boolean;
  mayRemove: boolean;
  mayDelegate: boolean;
  maySwap: boolean;
  mayNotify: boolean;
  mayCompleteShortfall: boolean;
  notice: FillNotice;
};

/**
 * ⚠️ [amended C3c.2] `canCompleteShortfall` IS A FOURTH **INPUT** AND IS REQUIRED, NOT DEFAULTED.
 *
 * It is a second capability rather than a consequence of the first — `mayAuthorArrangement`'s
 * rule for this family, and the reason the signature was widened rather than given a default:
 * `false` in the omitted position would silently withhold the control at a call site nobody
 * revisited, and `true` would offer a monitor's verb to every read-only role. Breaking both
 * callers is the fail-loud direction.
 */
export const fillControlsFor = (input: {
  canWrite: boolean;
  canCompleteShortfall: boolean;
  namingOpen: boolean;
  swapOpen: boolean;
}): FillControls => {
  const { canWrite, canCompleteShortfall, namingOpen, swapOpen } = input;
  /*
   * ⚠️ NAMING, REMOVING AND DELEGATING ARE ONE VERB FOR THIS PURPOSE. §3a: "no clicks, no saves,
   * no delegation". Removing is in the naming window rather than the swap one because
   * `removeAssignment` + `assignOfficer` were otherwise an unguarded swap — see the server.
   */
  const mayName = canWrite && namingOpen;

  return {
    mayName,
    mayRemove: mayName,
    mayDelegate: mayName,
    maySwap: canWrite && swapOpen,
    /*
     * ⚠️ COMPLETION IS GATED ONLY BY THE CAPABILITY, AND THE SERVER AGREES. `completeShares`
     * takes neither window: outstanding work is DERIVED and cleared only by ACTING ([C1g §1]),
     * and completion is the only clearing act — so a division that missed the deadline would
     * otherwise hold a share that is permanently red with nothing they can do about it.
     */
    /*
     * ⚠️ [amended C3c.1 §6b] `mayComplete` BECAME `mayNotify`, AND THE RULE IS UNCHANGED: THE
     * CAPABILITY ALONE, AND NEITHER WINDOW.
     *
     * §6a hides `Mark completed`, so the verb this field described no longer has a control, and
     * §9's no-dead-tooling rule reaches a boolean nothing renders. It is RENAMED rather than
     * deleted because the new verb sits in the same slot under the same rule: telling officers
     * about a duty names nobody, so the naming window does not bind — and it stays available
     * after `ends_at` for the reason `completeShares` did, that a nodal who missed the deadline
     * must still have an act available to them.
     *
     * ⚠️ AND IT HAS A PRODUCTION CONSUMER NOW, which `mayComplete` never did: that one was
     * computed and unit-tested while `DutyFill`'s button gated on the raw `readOnly`, so this
     * file's strongest claim was asserted about a value nothing rendered.
     */
    mayNotify: canWrite,
    /*
     * ⚠️ [C3c.2] NEITHER WINDOW, AND NOT `canWrite` EITHER — BOTH ABSENCES ARE DELIBERATE.
     *
     * No window, for `completeShares`' own reason above: closing a share is the only act that
     * clears outstanding work ([C1g §1]), so a division that missed the deadline must still
     * have it. Not `canWrite`, because the whole point of the capability behind this is that
     * its holder set is NOT `arrangement.write`'s — ANDing them would make a nodal monitor's
     * only verb unreachable, which is the requirement inverted.
     *
     * ⚠️ WHETHER THERE IS ANYTHING LEFT TO CLOSE IS DATA, NOT POLICY, and stays with the page —
     * `mayNotify` beside `notReady` is the same split, and it is what keeps this function a
     * table a unit test can drive with no fixture.
     */
    mayCompleteShortfall: canCompleteShortfall,
    /*
     * ⚠️ [amended C3c.1] FOUR REACHABLE COMBINATIONS OF TWO BOOLEANS, AND ALL FOUR ARE NAMED.
     * The old expression collapsed `namingOpen && !swapOpen` into `'none'`, which is the one
     * arm with a control missing from the screen and no sentence to explain it.
     */
    notice: !canWrite
      ? canCompleteShortfall
        ? 'shortfallOnly'
        : 'noCapability'
      : namingOpen && swapOpen
        ? 'none'
        : namingOpen
          ? 'swapClosed'
          : swapOpen
            ? 'namingClosed'
            : 'allClosed',
  };
};

/**
 * ⚠️ FOR A HOST THAT IS STRUCTURALLY READ-ONLY — `DutyRoster`, whose own header records that it
 * hard-codes the refusal rather than taking it as a prop, so "view only" is a property of the
 * FILE and not of a caller's argument. `notice: 'none'` because that surface explains itself.
 */
export const FILL_CONTROLS_NONE: FillControls = {
  mayName: false,
  mayRemove: false,
  mayDelegate: false,
  maySwap: false,
  mayNotify: false,
  mayCompleteShortfall: false,
  notice: 'none',
};
